import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import TrashIcon from 'icons/ic-trash.svg';
import { Trans, useTranslation } from 'react-i18next';
import voca from 'voca';

import { useUpdateBusinessSettingsMutation } from 'components/hooks/settingMutations';
import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';

import FilledButton from 'components/application/buttons/FilledButton';
import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CollectionSelect from 'components/application/CollectionSelect';
import ErrorMessage from 'components/application/ErrorMessage';
import SuccessToast from 'components/application/SuccessToast';
import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import Uploader from 'components/uploader/Uploader';

import { currencyCodes } from 'components/helpers/currency';

const defaultLogo = {
  data: '',
  src: '',
};

const parseDetails = (data) => ({
  address1: data.attributes.address1 || '',
  address2: data.attributes.address2 || '',
  address3: data.attributes.address3 || '',
  assignedAccountLanguageId:
    data.relationships.assignedAccountLanguage.data?.id || '',
  county: data.attributes.county || '',
  currencyCode: data.attributes.currencyCode || '',
  documentName: data.attributes.documentName || '',
  fax: data.attributes.fax || '',
  name: data.attributes.name || '',
  phone: data.attributes.phone || '',
  postcode: data.attributes.postcode || '',
  town: data.attributes.town || '',
  website: data.attributes.website || '',
});

export default function BusinessDetailsForm({ company }) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [
    formDetails,
    setFormDetails,
    handleDetailsInputChange,
    handleDetailsOptionChange,
  ] = useForm(parseDetails(company.data));

  const updateBusinessSettingsMutation = useUpdateBusinessSettingsMutation();

  const [isDocumentNameToggled, setIsDocumentNameToggled] = useState(false);

  const [logo, setLogo] = useState({
    ...defaultLogo,
    src: company.data.attributes.companyLogo.url,
  });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.businessDetails.components.BusinessDetailsForm',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const { data: domainAccountLanguages } = useQuery({
    enabled: currentActor.isAllowedFeature('multiple_languages'),
    queryKey: ['accountLanguages'],
    queryFn: async () => {
      const response = await axios.get('/account_languages');
      return response.data;
    },
  });

  const accountLanguageOptions =
    domainAccountLanguages?.data ?
      [
        {
          label: i18nT('localisation.language.options.accountDefault', {
            option: i18nT(
              `localisation.language.options.${domainAccountLanguages.data.find((accountLanguage) => accountLanguage.attributes.default).attributes.locale}`,
            ),
          }),
          value: '',
        },
      ].concat(
        domainAccountLanguages.data.map((accountLanguage) => ({
          label: i18nT(
            `localisation.language.options.${accountLanguage.attributes.locale}`,
          ),
          value: accountLanguage.id,
        })),
      )
    : [];

  const bodyRef = React.useRef();
  const hasLogoBeenModified = !!logo.src === !!logo.data;

  useEffect(() => {
    if (submitDisabled) bodyRef.current.scrollIntoView();
  }, [submitDisabled]);

  const handleLogoUpload = (fileData) => {
    setLogo({ data: fileData, src: URL.createObjectURL(fileData) });
  };

  const handleLogoDeletion = () => {
    setLogo(defaultLogo);
  };

  const detailsAsFormData = () => {
    const formData = new FormData();

    appendDetails();
    if (hasLogoBeenModified) appendLogo();

    return formData;

    function appendDetails() {
      Object.entries(formDetails).forEach((entry) => {
        const [key, value] = entry;

        formData.append(`company[${companyKey(key)}]`, value);
      });
    }

    function appendLogo() {
      formData.append('company[company_logo]', logo.data);
    }

    // simply calling voca.snakeCase wouldn't work here (address1, address2, address3)
    function companyKey(key) {
      switch (key) {
        case 'assignedAccountLanguageId':
        case 'currencyCode':
        case 'documentName':
          return voca.snakeCase(key);
        default:
          return key;
      }
    }
  };

  const handleSubmit = () => {
    updateBusinessSettingsMutation.mutate(
      { data: detailsAsFormData() },
      {
        onSuccess: (response) => {
          if (
            response.data.relationships.assignedAccountLanguage.data?.id !==
            company.data.relationships.assignedAccountLanguage.data?.id
          ) {
            window.location.href = '/settings/business/edit';
          } else {
            resetRequestError();

            setFormDetails(parseDetails(response.data));

            setLogo({
              data: '',
              src: response.data.attributes.companyLogo.url,
            });

            breadBoard.addToast(
              <SuccessToast
                message={
                  <Trans
                    components={{ span: <span className='tw-font-medium' /> }}
                    i18nKey='settings.businessDetails.components.BusinessDetailsForm.handleSubmit.success'
                    ns='components'
                  />
                }
              />,
            );
          }
        },
        onError: handleRequestError,
      },
    );
  };

  const currencyOptions = Object.keys(currencyCodes).map((currencyCode) => ({
    label: i18nT(`currencyCodeLabels.${currencyCode}`),
    value: currencyCode,
  }));

  const shouldShowCurrency =
    currentActor.isAllowedFeature('training_register') &&
    currentActor.division.attributes.primary;

  const shouldShowLanguages =
    currentActor.isAllowedFeature('multiple_languages') &&
    domainAccountLanguages?.data.length > 1;

  const shouldShowLocalisation = shouldShowCurrency || shouldShowLanguages;

  return (
    <div
      className='form__outer-container tw-border-grey-100 tw-bg-white'
      ref={bodyRef}
    >
      <div className='form__inner-container'>
        <div className='form__outer-section'>
          <div className='form__inner-section'>
            <ErrorMessage
              isFallback={requestError.isFallback}
              validationErrors={requestError.validationErrors}
              wrapperClassName='form__error-container'
            />
          </div>
          <div className='form__inner-section'>
            <div className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto'>
              {i18nT('businessNameAndLogo.title')}
            </div>
            <TextField
              fieldError={requestError.validationErrors.name}
              isRequired={true}
              label={i18nT('businessNameAndLogo.name.label')}
              name='name'
              onChange={handleDetailsInputChange}
              removeErrorStyling={removeErrorStyling}
              value={formDetails.name}
            />
            {formDetails.documentName || isDocumentNameToggled ?
              <TextField
                label={i18nT('businessNameAndLogo.documentName.label')}
                name='documentName'
                onChange={handleDetailsInputChange}
                value={formDetails.documentName}
              />
            : <div className='form-group'>
                <div
                  className='app-link tw-font-medium tw-text-blue-500 hover:tw-text-blue-300'
                  onClick={() => setIsDocumentNameToggled(true)}
                >
                  {i18nT('businessNameAndLogo.documentName.button')}
                </div>
              </div>
            }
            <div className='field__label tw-font-medium'>
              {i18nTShared('logo')}
            </div>
            <div className='form__uploader' id='business-details__logo'>
              {logo.src ?
                <>
                  <div className='form__image-container tw-border-grey-100'>
                    <img
                      alt={i18nT('businessNameAndLogo.logo.imgAlt')}
                      className='image--bound-scaling'
                      src={logo.src}
                    />
                  </div>
                  <div className='form__image-action-buttons'>
                    <OutlinedButton
                      color='red'
                      onClick={handleLogoDeletion}
                      size='sm'
                    >
                      <TrashIcon className='tw-mr-1' height={20} width={20} />
                      <span>{i18nTShared('deleteLogo')}</span>
                    </OutlinedButton>
                  </div>
                </>
              : <Uploader
                  externalInputProps={{ name: 'company[logo]' }}
                  fileTypes={['JPG', 'PNG']}
                  mode='solo'
                  onUpload={handleLogoUpload}
                  uploadsCount={0}
                />
              }
            </div>
          </div>

          <hr className='tw-my-10 tw-h-px tw-border-0 tw-bg-grey-100' />

          <div className='form__inner-section'>
            <div className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
              {i18nT('businessAddress.title')}
            </div>
            <TextField
              label={i18nT('businessAddress.address1.label')}
              name='address1'
              onChange={handleDetailsInputChange}
              value={formDetails.address1}
            />
            <TextField
              label={i18nT('businessAddress.address2.label')}
              name='address2'
              onChange={handleDetailsInputChange}
              value={formDetails.address2}
            />
            <TextField
              label={i18nT('businessAddress.address3.label')}
              name='address3'
              onChange={handleDetailsInputChange}
              value={formDetails.address3}
            />
            <TextField
              label={i18nT('businessAddress.town.label')}
              name='town'
              onChange={handleDetailsInputChange}
              value={formDetails.town}
            />
            <TextField
              label={i18nT('businessAddress.county.label')}
              name='county'
              onChange={handleDetailsInputChange}
              value={formDetails.county}
            />
            <TextField
              label={i18nT('businessAddress.postcode.label')}
              name='postcode'
              onChange={handleDetailsInputChange}
              value={formDetails.postcode}
            />
          </div>

          {shouldShowLocalisation && (
            <>
              <hr className='tw-my-10 tw-h-px tw-border-0 tw-bg-grey-100' />

              <div className='form__inner-section'>
                <div className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
                  {i18nT('localisation.title')}
                </div>
                <div className='tw-flex tw-flex-col tw-gap-[15px]'>
                  {shouldShowCurrency && (
                    <div>
                      <CollectionSelect
                        isSearchable={true}
                        label={i18nT('localisation.currencyCode.label')}
                        name='currencyCode'
                        onChange={handleDetailsOptionChange}
                        options={currencyOptions}
                        value={currencyOptions.find(
                          (option) => option.value === formDetails.currencyCode,
                        )}
                      />
                      <p className='tw-mb-0 tw-mt-4 tw-text-s tw-tracking-wide'>
                        <Trans
                          components={{
                            span: <span className='tw-font-semibold' />,
                          }}
                          i18nKey='settings.businessDetails.components.BusinessDetailsForm.localisation.currencyCode.description'
                          ns='components'
                        />
                      </p>
                    </div>
                  )}
                  {shouldShowLanguages && (
                    <CollectionSelect
                      label={i18nT('localisation.language.label')}
                      name='assignedAccountLanguageId'
                      onChange={handleDetailsOptionChange}
                      options={accountLanguageOptions}
                      value={accountLanguageOptions?.find(
                        (accountLanguageOption) =>
                          accountLanguageOption.value ===
                          formDetails.assignedAccountLanguageId,
                      )}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          <hr className='tw-my-10 tw-h-px tw-border-0 tw-bg-grey-100' />

          <div className='form__inner-section'>
            <div className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
              {i18nT('businessContactDetails.title')}
            </div>
            <TextField
              label={i18nT('businessContactDetails.phone.label')}
              name='phone'
              onChange={handleDetailsInputChange}
              value={formDetails.phone}
            />
            <TextField
              label={i18nT('businessContactDetails.fax.label')}
              name='fax'
              onChange={handleDetailsInputChange}
              value={formDetails.fax}
            />
            <TextField
              label={i18nT('businessContactDetails.website.label')}
              name='website'
              onChange={handleDetailsInputChange}
              value={formDetails.website}
            />
          </div>
        </div>
      </div>
      <div className='form__outer-section'>
        <div className='form__footer tw-border-grey-100'>
          <FilledButton
            color='blue'
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            {i18nTShared('saveChanges')}
          </FilledButton>
        </div>
      </div>
    </div>
  );
}
